import React, { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Col,
  Row,
  Label,
} from "reactstrap";
import TextEditor from "components/editor/TextEditor";
import { useFormik } from "formik";
import * as Yup from "yup";
import { httpRequest } from "utils/httpRequest";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom"

const AddArticle = () => {


  const [imageLink, setImageLink] = useState(null)
  const [buttonLoading, setButtonLoading] = useState(false);

  const navigate = useNavigate()




  // Validation Schema
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Article title is required")
      .min(3, "Title must be at least 3 characters"),
    description: Yup.string()
      .required("Description is required")
      .test('not-empty-html', 'Description is required', function (value) {
        if (!value) return false;
        // Remove HTML tags and check if there's actual content
        const textOnly = value.replace(/<[^>]*>/g, '').trim();
        return textOnly.length > 0;
      }),

    image: Yup.mixed()
      .required("Cover image is required")

      .test(
        "fileSize",
        "Image must be smaller than 5MB",
        (value) => value && value.size <= 5000000
      )
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      image: null
    },
    validationSchema,
    validateOnChange: false,  // Disable auto validation on change
    validateOnBlur: false,
    onSubmit: async (values) => {

      formik.setTouched({

        title: true,
        description: true,
        image: true
      });

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) return false;
      setButtonLoading(true)
      try {
        const uploadedImageLink = await getUploadedImageLink(values.image);

        const response = await httpRequest(
          "article/add",
          "POST",
          {
            title: values.title,
            description: values.description,
            image: imageLink
          },
          {},
          true,
          true
        );
        if (response?.code === 200) {
          
          toast.success("Article added successfully.")
          formik.resetForm({
            values: {
              title: "",
              description: "",
              image: null
            }
          })

          navigate("/admin/article-management")

        }

      } catch (error) {
        console.log(error);

      } finally {
        setButtonLoading(false)
      }


    },
  });






  const getUploadedImageLink = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await httpRequest(
        "admin/UploadMedia",
        "POST",
        formData,
        {},
        true,
        true
      );
      return response?.filePath;
    } catch (error) {
      console.log("Upload error:", error);
      throw error;
    }
  };




  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    formik.setFieldError("image", undefined);

    // First update Formik state and show preview
    await formik.setFieldValue("image", file, false);
    const imageUrl = URL.createObjectURL(file);
    formik.setFieldValue("imagePreview", imageUrl, false);

    // Then upload the image after Formik state is updated
    try {
      const uploadedImageLink = await getUploadedImageLink(file); // Use the file directly
      setImageLink(uploadedImageLink);

      if (formik.submitCount > 0) { // Only validate if form was submitted before
        await formik.validateField("image");
      }
    } catch (error) {
      console.log("Upload failed:", error);
    }
  };





  return (
    <Row className="mt-4 justify-content-center">
      <Col lg="8">
        <Card className="bg-secondary shadow border-0">
          <CardHeader className="bg-transparent pb-3">
            <div className="text-muted text-center mt-2 mb-3">
              <small>Add New Article</small>
            </div>
          </CardHeader>
          <CardBody className="px-lg-5 py-lg-4">
            <Form onSubmit={formik.handleSubmit}>
              {/* Article Title */}
              <FormGroup className="mb-3">
                <Label className="form-control-label">Article Title</Label>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-book-bookmark" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Enter Article title"
                    type="text"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small mt-1">
                    {formik.errors.title}
                  </div>
                )}
              </FormGroup>

              {/* Article Description */}
              <FormGroup>
                <Label className="form-control-label">Article Description</Label>
                <TextEditor
                  value={formik.values.description}
                  onChange={(html) => formik.setFieldValue("description", html)}
                  onBlur={() => formik.setFieldTouched("description", true)}
                  placeholder="Write the article description..."
                  hideImageAndLink={true}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="text-danger small mt-1">
                    {formik.errors.description}
                  </div>
                )}
              </FormGroup>

              {/* Image Upload */}
              <FormGroup className="mb-4">
                <Label className="form-control-label" htmlFor="imageUpload">
                  Upload Article Cover Image
                </Label>
                <input
                  type="file"
                  name="image"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleImageChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.image && formik.errors.image && (
                  <div className="text-danger small mt-1">
                    {formik.errors.image}
                  </div>
                )}
              </FormGroup>

              {formik.values.imagePreview && (
                <div className="mt-3">
                  <img
                    src={formik.values.imagePreview}
                    alt="Preview"
                    style={{ maxWidth: "25%", height: "auto", borderRadius: "0.25rem" }}
                  />
                </div>
              )}

              <div className="text-center d-flex align-items-center justify-content-center">
                <Button className="mb-3 d-flex align-items-center justify-content-center" color="primary" type="submit" disabled={buttonLoading}>
                  {buttonLoading ? <span className="button-loader"></span> : "Save Article"}
                </Button>

              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default AddArticle;